import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDownRight } from "lucide-react";
import NodeNetwork from "@/components/site/NodeNetwork";
import { MaskedLines } from "@/components/site/Reveal";
import { COMPANY, HERO_LINES } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    const t = setTimeout(() => setStart(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      ref={ref}
      className="relative flex min-h-screen w-full flex-col justify-end overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Node network background w/ parallax */}
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <NodeNetwork />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </motion.div>

      {/* Grid skeleton */}
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-40" />

      {/* Overline top */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: start ? 1 : 0 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute left-5 top-28 flex items-center gap-3 md:left-10 md:top-32"
      >
        <span className="h-px w-10 bg-[#0033ff]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa] md:text-xs">
          {COMPANY.name}
        </span>
      </motion.div>

      {/* Headline */}
      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-16 md:px-10 md:pb-24"
      >
        <h1 className="font-display text-[12vw] font-extrabold uppercase leading-[0.9] tracking-tighter text-[#f4f4f5] md:text-[9vw] lg:text-[7.5vw]">
          <MaskedLines
            lines={HERO_LINES}
            start={start}
            lineClassName="whitespace-nowrap"
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.76, 0, 0.24, 1] }}
          className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-6 md:flex-row md:items-end"
        >
          <p className="max-w-md font-mono text-sm leading-relaxed text-[#a1a1aa]">
            Enterprise integration engineered on webMethods &amp; Boomi — the
            connective tissue for global logistics, healthcare and agriculture.
          </p>
          <button
            onClick={() =>
              lenis
                ? lenis.scrollTo("#services", { offset: -20 })
                : document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })
            }
            data-testid="hero-explore-btn"
            className="group flex items-center gap-3 border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-[#0033ff] hover:bg-[#0033ff]"
          >
            Explore capabilities
            <ArrowDownRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
